import {
  Alert,
  Badge,
  Box,
  Gap,
  ImageSelect,
  Modal,
  P,
  RadioButton,
  RadioGroup,
  Search,
  SelectMini,
  Spinner,
} from '@salecycle/ui-component-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';

const scrollableAreaHeight = 520;

const TemplateArea = styled.div`
  height: 424px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  gap: 20px;
`;

const TemplateItem = styled.div`
  display: inline-block;
`;

const TextPreviewCardBorder = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  height: 260px;
  width: 260px;
  cursor: pointer;
  border: ${({ selected, theme }) =>
    selected ? `2px solid ${theme.vars.newColor.blue1}` : `1px solid ${theme.vars.newColor.lightGS1}`};
  padding: ${({ selected }) => (selected ? 0 : '1px')};
  &:hover {
    border: ${({ selected, theme }) => !selected && `1px solid ${theme.vars.newColor.blue3}`};
  }
`;

const TextPreviewContent = styled.div`
  flex: 1;
  min-height: 0;
  padding: 12px;
  overflow-y: auto;
  border-bottom: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.vars.newColor.darkGS1};
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
`;

function TextPreviewCard<T extends Template>({
  template,
  previewContent,
  createdAt,
  selected,
  onSelect,
  badge,
  createdLabel,
  atLabel,
}: {
  template: T;
  previewContent: string;
  createdAt: Date;
  selected: boolean;
  onSelect: () => void;
  badge?: string;
  createdLabel: string;
  atLabel: string;
}) {
  const theme = useTheme();
  return (
    <TextPreviewCardBorder selected={selected} onClick={onSelect}>
      <TextPreviewContent>{previewContent || ' '}</TextPreviewContent>
      <div style={{ padding: 16, minHeight: 140 }}>
        <Box stackItems>
          <RadioButton label={template.templateName} selected={selected} id={template.id} style={{ margin: 0 }} />
          <P
            color={theme.vars.newColor.darkGS2}
            fontSize="18px"
            style={{
              fontSize: 12,
              lineHeight: '18px',
              fontWeight: 375,
              height: 34,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {createdLabel}
            {` ${createdAt.toLocaleDateString()} ${atLabel} ${createdAt.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short',
            })}`}
          </P>
        </Box>
        {badge && (
          <Box style={{ marginTop: theme.designToken.spacing3 }}>
            <Badge
              size="small"
              rounded={4}
              style={{
                color: theme.designToken.primary700,
                backgroundColor: theme.designToken.primary50,
                border: 'none',
              }}
            >
              {badge}
            </Badge>
          </Box>
        )}
      </div>
    </TextPreviewCardBorder>
  );
}

const SpinnerWrapper = styled.div<{ minHeight: number }>`
  min-height: ${props => props.minHeight};
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  margin: 32px 0;
  position: relative;
  z-index: 1;
`;

const EmptyStateArea = styled.div`
  min-height: ${scrollableAreaHeight}px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.designToken.spacing4};
  box-sizing: border-box;
`;

const EmptyStateAlertInner = styled.div`
  width: 100%;
  max-width: 720px;
`;

const EmptyStateAlertDescription = styled.div`
  font: ${({ theme }) => theme.designToken.body1};
  color: ${({ theme }) => theme.designToken.neutral700};
  line-height: 1.5;
`;

type SortDirection = 'ascending' | 'descending';

function SortByDate({
  onSort,
  direction,
  t,
}: {
  onSort: (direction: SortDirection) => void;
  direction: SortDirection;
  t: TFunction;
}) {
  const flipDirection = useCallback(() => {
    const sortDirection = direction === 'ascending' ? 'descending' : 'ascending';
    onSort(sortDirection);
  }, [direction, onSort]);

  const sortDirection = useMemo(() => {
    const texts: { [key in SortDirection]: string } = {
      ascending: t('oldToNew'),
      descending: t('newToOld'),
    };
    const icons: { [key in SortDirection]: JSX.Element } = {
      ascending: <FontAwesomeIcon icon={faArrowUp as IconProp} style={{ pointerEvents: 'none' }} />,
      descending: <FontAwesomeIcon icon={faArrowDown as IconProp} style={{ pointerEvents: 'none' }} />,
    };
    return {
      text: texts[direction],
      icon: icons[direction],
    };
  }, [direction, t]);

  return (
    <button
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        marginRight: '16px',
        cursor: 'pointer',
        padding: 0,
        border: 'none',
        background: 'none',
        font: 'inherit',
      }}
      onClick={flipDirection}
    >
      <P>
        {t('sortBy')}{' '}
        <span style={{ fontWeight: '600' }}>
          {t('date')} ({sortDirection.text})
        </span>
      </P>
      <Gap right={5} />
      <span>{sortDirection.icon}</span>
    </button>
  );
}

export interface Template {
  id: string;
  type: string;
  channel: string;
  templateName: string;
  isEmbedded: boolean;
  audit: {
    createdAt: string;
  };
}

export interface TemplateQuery<TTemplate extends Template> {
  data: TTemplate[];
  isLoading: boolean;
  isError: boolean;
}

export interface TemplateSelectorModalProps<TTemplate extends Template> {
  /** The language pack function. Used to retrieve the language packs. */
  t: TFunction;
  /** Determines whether the modal is shown/hidden. */
  open: boolean;
  /** The close callback. Called once the modal has been closed. */
  onClose: () => void;
  /** The confirm callback. The given template is the selected template. */
  onConfirm: (template: Template) => void;
  /** The template query. The query will be used to fetch the templates and a loding indicator will be shown during the request.  */
  templatesQuery: TemplateQuery<TTemplate>;
  /** A list of template types that can be filtered by. Each given template type will be used as the key for the langauge pack lookup. */
  templateTypes: string[];
  /** The fallback teample image URL. */
  templateFallbackThumbnailUrl?: string;
  /** Gets the template thumbnail for the given template. Not needed when using getTemplatePreviewContent (e.g. SMS). */
  getTemplateThumbnailUrl?: (template: Template) => string;
  /** If provided, only templates from the given channel will be shown. */
  channel: 'onsite' | 'offsite' | 'email' | 'sms';
  /** If provided, templates will be filtered by the given template type and the filter dropdown will be populated. */
  defaultTemplateTypeFilter?: string;
  /** If provided, the given template will be selected. **/
  defaultTemplateId?: string;
  /** If true, the embed details are shown. Hidden otherwise. */
  isEmbedFlagEnabled?: boolean;
  /** When provided, the template card shows this text content instead of a thumbnail (e.g. for SMS templates). */
  getTemplatePreviewContent?: (template: TTemplate) => string | undefined;
  /**
   * When the template list is empty (e.g. no SMS templates for the selected language), shown in place of the grid.
   */
  emptyStateMessage?: string;
}

export function TemplateSelectorModal<TTemplate extends Template>({
  open,
  onClose,
  defaultTemplateTypeFilter,
  defaultTemplateId,
  t,
  templatesQuery,
  templateTypes,
  getTemplateThumbnailUrl,
  templateFallbackThumbnailUrl,
  onConfirm,
  channel,
  isEmbedFlagEnabled,
  getTemplatePreviewContent,
  emptyStateMessage,
}: TemplateSelectorModalProps<TTemplate>) {
  const templateFilterOptions = useMemo(() => {
    const options = templateTypes.map(s => ({
      label: t(s),
      value: s,
    }));

    return [
      {
        label: t('showAll'),
        value: '',
      },

      ...options,
    ];
  }, [t, templateTypes]);

  const searchRef = useRef(null);

  const [sortDirection, setSortDirection] = useState<SortDirection>('descending');
  const [search, setSearch] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(defaultTemplateId);

  useEffect(() => {
    if (open) {
      setSelectedTemplateId(defaultTemplateId ?? null);
    }
  }, [open, defaultTemplateId]);

  const templateTypeOnLoad = useMemo(() => {
    if (!defaultTemplateTypeFilter) {
      // Default to 'Show All'
      return templateFilterOptions[0];
    }

    return templateFilterOptions.find(s => s.value === defaultTemplateTypeFilter);
  }, [defaultTemplateTypeFilter, templateFilterOptions]);

  const [templateType, setfilteredTemplate] = useState(templateTypeOnLoad);

  const onSort = useCallback((direction: SortDirection) => setSortDirection(direction), []);

  const sortedTemplates = useMemo(() => {
    let templates = templatesQuery.data;

    if (search) {
      templates = templates.filter(s => s.templateName.toLocaleLowerCase().search(search.toLocaleLowerCase()) !== -1);
    }

    return templates;
  }, [templatesQuery.data, search]);

  const onSearch = useCallback((value: string) => setSearch(value || ''), [setSearch]);
  const onClearSearch = () => {
    setSearch('');
    searchRef.current.value = '';
  };

  const handleTemplateChange = (option: any) => {
    if (option) {
      setfilteredTemplate(option);
    }
  };

  const onSelected = useCallback((id: string) => {
    setSelectedTemplateId(id);
  }, []);

  const handleOnClose = useCallback(() => {
    setSelectedTemplateId(null);
    setSearch('');
    onClose();
  }, [onClose]);

  const handleOnConfirm = useCallback(() => {
    const template = sortedTemplates.find(s => s.id === selectedTemplateId);
    onConfirm(template);
    handleOnClose();
  }, [handleOnClose, onConfirm, selectedTemplateId, sortedTemplates]);

  function filteredTemplates() {
    const meetsChannelCriteria = (method: string): boolean => {
      if (channel === 'onsite') return method === 'onsite';
      if (channel === 'sms' || channel === 'email') return method === channel;
      return method !== 'onsite';
    };

    if (templateType.value !== '') {
      return sortedTemplates.filter(item => item.type === templateType.value && meetsChannelCriteria(item.channel));
    }

    return sortedTemplates.filter(item => meetsChannelCriteria(item.channel));
  }

  const getBadgeLabel = (s: Template) => {
    if (!isEmbedFlagEnabled) {
      return;
    }

    if (s.channel === 'email' || s.channel === 'sms') {
      return;
    }

    return s.isEmbedded ? t('embed') : t('popUp');
  };

  const heading = channel === 'sms' ? t('headingSms') : t('heading');
  const modalAria = channel === 'sms' ? t('modalAriaSms') : t('modalAria');
  const modalDescription = channel === 'sms' ? t('modalDescriptionSms') : t('modalDescription');

  const hasNoTemplates =
    !templatesQuery.isLoading && !templatesQuery.isError && !(templatesQuery.data && templatesQuery.data.length > 0);
  const showEmptyState = Boolean(emptyStateMessage && hasNoTemplates);

  return (
    <Modal
      minWidth={900}
      minHeight={640}
      heading={heading}
      aria-label={modalAria}
      isOpen={open}
      variation="clean"
      onCloseButtonClicked={handleOnClose}
      onOkButtonClicked={handleOnConfirm}
      okButtonAriaLabel={t('confirm')}
      okButtonLabel={t('confirm')}
      okButtonDisabled={!selectedTemplateId}
      cancelButtonLabel={t('cancel')}
    >
      {templatesQuery.isLoading && (
        <SpinnerWrapper minHeight={scrollableAreaHeight}>
          <Spinner />
        </SpinnerWrapper>
      )}
      {!templatesQuery.isLoading && !templatesQuery.isError && (
        <>
          <P style={{ margin: 0 }}>{modalDescription}</P>
          <Gap bottom={32} />
          {showEmptyState ? (
            <EmptyStateArea role="status" aria-live="polite">
              <EmptyStateAlertInner>
                <Alert
                  variation="info"
                  heading={t('emptySmsTemplatesForLanguageHeading')}
                  description={<EmptyStateAlertDescription>{emptyStateMessage}</EmptyStateAlertDescription>}
                />
              </EmptyStateAlertInner>
            </EmptyStateArea>
          ) : (
            <>
              <Search
                innerRef={searchRef}
                value={search}
                placeholder={t('searchPlaceholder')}
                onChange={onSearch}
                onClearButtonClicked={onClearSearch}
                fullWidth
              />
              <FilterRow>
                <SortByDate onSort={onSort} direction={sortDirection} t={t} />
                <SelectMini
                  options={templateFilterOptions}
                  onChange={handleTemplateChange}
                  radioButtonMenu={true}
                  value={templateType}
                  ariaLabel={t('templateTypeSelectAria')}
                  label={t('templateTypeSelectLabel')}
                />
              </FilterRow>
              <TemplateArea>
                <RadioGroup onRadioButtonSelected={onSelected}>
                  {filteredTemplates()
                    .sort((a, b) =>
                      sortDirection === 'descending'
                        ? Number.parseInt(b.audit.createdAt) - Number.parseInt(a.audit.createdAt)
                        : Number.parseInt(a.audit.createdAt) - Number.parseInt(b.audit.createdAt),
                    )
                    .map((s, i) => {
                      const createdAt = new Date(Number.parseInt(s.audit.createdAt));
                      const previewContent = getTemplatePreviewContent?.(s);
                      const useTextPreview = getTemplatePreviewContent && previewContent !== undefined;

                      if (useTextPreview) {
                        return (
                          <TemplateItem key={s.id}>
                            <TextPreviewCard
                              template={s}
                              previewContent={previewContent}
                              createdAt={createdAt}
                              selected={selectedTemplateId === s.id}
                              onSelect={() => onSelected(s.id)}
                              badge={getBadgeLabel(s)}
                              createdLabel={t('created')}
                              atLabel={t('at')}
                            />
                          </TemplateItem>
                        );
                      }

                      return (
                        <TemplateItem key={s.id}>
                          <ImageSelect
                            id={s.id}
                            src={getTemplateThumbnailUrl?.(s) ?? templateFallbackThumbnailUrl}
                            fallback={templateFallbackThumbnailUrl}
                            templateName={s.templateName}
                            createdAt={createdAt.toString()}
                            badge={getBadgeLabel(s)}
                            aria-label={t('templateItemAria')}
                          />
                        </TemplateItem>
                      );
                    })}
                </RadioGroup>
              </TemplateArea>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
